<script lang="ts">
import type { HTMLAttributes } from 'vue'

interface AppDocueAgentLinkProps {
  class?: HTMLAttributes['class']
  href?: string
  target?: string
}

const DOCUE_ENTITY_TYPES = ['document', 'folder', 'template'] as const
type DocueEntityType = typeof DOCUE_ENTITY_TYPES[number]
</script>

<script setup lang="ts">
defineOptions({
  inheritAttrs: false,
})

const props = defineProps<AppDocueAgentLinkProps>()
const currentUrl = useRequestURL()
const isDocueLink = computed(() => /^docue:/i.test(props.href ?? ''))

function isDocueEntityType(value: string): value is DocueEntityType {
  return DOCUE_ENTITY_TYPES.includes(value as DocueEntityType)
}

const entity = computed(() => {
  if (!props.href?.startsWith('docue://'))
    return undefined

  try {
    const url = new URL(props.href)
    const type = url.hostname
    const id = decodeURIComponent(url.pathname.slice(1))

    if (
      url.protocol !== 'docue:'
      || !isDocueEntityType(type)
      || !id
      || id.includes('/')
      || url.username
      || url.password
      || url.port
      || url.search
      || url.hash
    ) {
      return undefined
    }

    return { id, type }
  }
  catch {
    return undefined
  }
})

// NuxtLink treats any absolute URL as external. Rewrite same-host absolute
// URLs to a path so chat links stay on client-side navigation.
const to = computed(() => {
  if (!props.href)
    return ''

  try {
    const url = new URL(props.href, currentUrl)
    if (url.host === currentUrl.host)
      return `${url.pathname}${url.search}${url.hash}`
  }
  catch {
    // fall through with the original href
  }

  return props.href
})
</script>

<template>
  <AppDocueAgentEntity
    v-if="entity"
    :id="entity.id"
    :type="entity.type"
  >
    <slot />
  </AppDocueAgentEntity>
  <span v-else-if="isDocueLink"><slot /></span>
  <NuxtLink
    v-else
    v-bind="$attrs"
    :to
    :target
    :class="props.class"
  >
    <slot />
  </NuxtLink>
</template>
